import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { logOut } from '../lib/firebase'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  getCurrentUser,
  createCheckoutSession,
  createPortalSession,
} from '../lib/api'
import {
  User,
  Moon,
  Sun,
  Bell,
  Shield,
  CreditCard,
  HelpCircle,
  LogOut,
  ChevronRight,
  Sparkles,
  ExternalLink,
  Loader2,
  Check,
  Crown,
  Zap,
  AlertCircle,
  RefreshCw,
  FileText,
  MessageSquare,
  HardDrive,
  X,
  Smartphone,
} from 'lucide-react'

function SettingSection({ title, children }) {
  return (
    <div className="mb-8">
      <h2 className="text-sm font-medium text-muted-foreground mb-3 px-1">
        {title}
      </h2>
      <div className="bg-card rounded-2xl border border-border overflow-hidden">
        {children}
      </div>
    </div>
  )
}

function SettingItem({ icon: Icon, label, description, action, onClick, danger }) {
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-4 p-4 hover:bg-accent/50 transition-colors text-left border-b border-border last:border-0 ${
        danger ? 'text-destructive hover:bg-destructive/10' : ''
      }`}
    >
      <div className={`p-2 rounded-xl ${danger ? 'bg-destructive/10' : 'bg-primary/10'}`}>
        <Icon className={`w-5 h-5 ${danger ? 'text-destructive' : 'text-primary'}`} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-medium">{label}</p>
        {description && (
          <p className="text-sm text-muted-foreground">{description}</p>
        )}
      </div>
      {action || <ChevronRight className="w-5 h-5 text-muted-foreground" />}
    </button>
  )
}

// Stripe price IDs - use environment variables with fallback to production
const STRIPE_PRICES = {
  monthly: import.meta.env.VITE_STRIPE_PRICE_MONTHLY || 'price_1Sodz8K8pyKtzvCYEIZKKwJq',
  yearly: import.meta.env.VITE_STRIPE_PRICE_YEARLY || 'price_1Soe0oK8pyKtzvCYQhmKmAld',
}

// Plan configuration
const PLANS = {
  free: {
    name: 'Free',
    price: '$0',
    period: 'forever',
    features: [
      '50 content items',
      '10 AI chats per month',
      'Basic search',
      '100MB storage',
    ],
    color: 'from-gray-400 to-gray-500',
  },
  premium: {
    name: 'Premium',
    price: '$8.99',
    period: 'per month',
    priceId: STRIPE_PRICES.monthly,
    features: [
      'Unlimited content',
      'Unlimited AI chats',
      'Advanced semantic search',
      '10GB storage',
      'Priority support',
      'API access',
    ],
    color: 'from-primary to-purple-500',
  },
  premium_yearly: {
    name: 'Premium Yearly',
    price: '$80.99',
    period: 'per year',
    priceId: STRIPE_PRICES.yearly,
    savings: 'Save $27',
    features: [
      'Everything in Premium',
      '2 months free',
    ],
    color: 'from-amber-500 to-orange-500',
  },
}

// Usage meter for free plan - shows progress bar
function UsageMeter({ icon: Icon, label, current, max, color }) {
  const percentage = max > 0 ? Math.min((current / max) * 100, 100) : 0
  const isNearLimit = percentage >= 80

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4" style={{ color }} />
          <span className="text-sm font-medium">{label}</span>
        </div>
        <span className={`text-sm font-semibold ${isNearLimit ? 'text-destructive' : 'text-muted-foreground'}`}>
          {current} / {max}
        </span>
      </div>
      <div className="h-2 bg-muted rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all"
          style={{
            width: `${percentage}%`,
            backgroundColor: isNearLimit ? 'hsl(var(--destructive))' : color,
          }}
        />
      </div>
    </div>
  )
}

// Stat row for premium plan - simple label/value display
function StatRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center justify-between py-2">
      <div className="flex items-center gap-2.5 text-muted-foreground">
        <Icon className="w-4 h-4" />
        <span className="text-sm">{label}</span>
      </div>
      <span className="text-sm font-semibold">{value}</span>
    </div>
  )
}

// Payment provider badge component
function PaymentProviderBadge({ provider }) {
  if (provider === 'appstore') {
    return (
      <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gray-900 dark:bg-white text-white dark:text-gray-900 text-xs font-medium">
        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor">
          <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
        </svg>
        App Store
      </div>
    )
  }

  if (provider === 'stripe') {
    return (
      <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#635BFF] text-white text-xs font-medium">
        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor">
          <path d="M13.976 9.15c-2.172-.806-3.356-1.426-3.356-2.409 0-.831.683-1.305 1.901-1.305 2.227 0 4.515.858 6.09 1.631l.89-5.494C18.252.975 15.697 0 12.165 0 9.667 0 7.589.654 6.104 1.872 4.56 3.147 3.757 4.992 3.757 7.218c0 4.039 2.467 5.76 6.476 7.219 2.585.92 3.445 1.574 3.445 2.583 0 .98-.84 1.545-2.354 1.545-1.875 0-4.965-.921-6.99-2.109l-.9 5.555C5.175 22.99 8.385 24 11.714 24c2.641 0 4.843-.624 6.328-1.813 1.664-1.305 2.525-3.236 2.525-5.732 0-4.128-2.524-5.851-6.591-7.305z"/>
        </svg>
        Stripe
      </div>
    )
  }

  if (provider === 'google_play') {
    return (
      <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#34A853] text-white text-xs font-medium">
        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor">
          <path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 0 1-.61-.92V2.734a1 1 0 0 1 .609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.198l2.807 1.626a1 1 0 0 1 0 1.73l-2.807 1.626L15.206 12l2.492-2.491zM5.864 2.658L16.8 8.99l-2.302 2.302-8.634-8.634z"/>
        </svg>
        Google Play
      </div>
    )
  }

  return null
}

function SubscriptionCard({ subscription, usage, onUpgrade, onManage, onManageApple, loading }) {
  const plan = subscription?.plan || 'free'
  const isPremium = plan !== 'free'
  const subscriptionName = subscription?.subscriptionName || (isPremium ? 'Premium' : null)
  const paymentProvider = subscription?.paymentProvider // 'stripe' | 'apple' | null
  const expiresAt = subscription?.expiresAt

  // Format expiration date
  const formatExpirationDate = (dateStr) => {
    if (!dateStr) return null
    const date = new Date(dateStr)
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  return (
    <div className={`p-4 rounded-2xl border ${
      isPremium
        ? 'bg-gradient-to-br from-primary/10 via-primary/5 to-transparent border-primary/30'
        : 'bg-muted/30 border-border'
    }`}>
      {/* Plan Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`p-2.5 rounded-xl ${
            isPremium ? 'bg-primary/15' : 'bg-amber-500/15'
          }`}>
            {isPremium ? (
              <Crown className={`w-5 h-5 text-primary`} />
            ) : (
              <Sparkles className="w-5 h-5 text-amber-500" />
            )}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <p className={`font-bold ${isPremium ? 'text-primary' : 'text-amber-600'}`}>
                {isPremium ? 'Premium Plan' : 'Free Plan'}
              </p>
              {isPremium && paymentProvider && (
                <PaymentProviderBadge provider={paymentProvider} />
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              {isPremium
                ? expiresAt
                  ? `Renews ${formatExpirationDate(expiresAt)}`
                  : (subscriptionName || 'Premium')
                : 'Limited features'
              }
            </p>
          </div>
        </div>

        {/* Upgrade button for free users */}
        {!isPremium && (
          <button
            onClick={onUpgrade}
            disabled={loading}
            className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-primary text-white text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-50"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                <Zap className="w-4 h-4" />
                Upgrade
              </>
            )}
          </button>
        )}

        {loading && isPremium && (
          <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
        )}
      </div>

      {/* Usage Section */}
      {usage && (
        <div className="space-y-3">
          {!isPremium ? (
            // Free plan: Show usage meters with progress bars
            <>
              <UsageMeter
                icon={FileText}
                label="Daily Content"
                current={usage.contentCount || 0}
                max={usage.contentCap || 50}
                color="#3b82f6"
              />
              <UsageMeter
                icon={MessageSquare}
                label="Daily AI Prompts"
                current={usage.chatCount || 0}
                max={usage.chatCap || 10}
                color="#a855f7"
              />
            </>
          ) : (
            // Premium plan: Show simple stats
            <>
              <StatRow
                icon={HardDrive}
                label="Storage Used"
                value={`${usage.storageCount || 0} MB`}
              />
              <StatRow
                icon={FileText}
                label="Content Created"
                value={`${usage.contentCount || 0}`}
              />
              <StatRow
                icon={MessageSquare}
                label="AI Chats Today"
                value={`${usage.chatCount || 0}`}
              />
            </>
          )}
        </div>
      )}

      {/* Manage button for premium users - different based on payment provider */}
      {isPremium && (
        <>
          {paymentProvider === 'appstore' ? (
            <div className="mt-4 space-y-3">
              <div className="p-3 rounded-xl bg-muted/50 border border-border">
                <div className="flex items-start gap-3">
                  <Smartphone className="w-5 h-5 text-muted-foreground mt-0.5 flex-shrink-0" />
                  <div className="text-sm">
                    <p className="font-medium text-foreground">Subscribed via App Store</p>
                    <p className="text-muted-foreground mt-1">
                      To manage your subscription, open Settings on your iPhone or iPad, tap your name, then Subscriptions.
                    </p>
                  </div>
                </div>
              </div>
              <button
                onClick={() => window.open('https://apps.apple.com/account/subscriptions', '_blank')}
                className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-gray-900 dark:bg-white text-white dark:text-gray-900 hover:opacity-90 transition-opacity text-sm font-medium"
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                </svg>
                Manage in App Store
                <ExternalLink className="w-3.5 h-3.5" />
              </button>
            </div>
          ) : paymentProvider === 'google_play' ? (
            <div className="mt-4 space-y-3">
              <div className="p-3 rounded-xl bg-muted/50 border border-border">
                <div className="flex items-start gap-3">
                  <Smartphone className="w-5 h-5 text-muted-foreground mt-0.5 flex-shrink-0" />
                  <div className="text-sm">
                    <p className="font-medium text-foreground">Subscribed via Google Play</p>
                    <p className="text-muted-foreground mt-1">
                      To manage your subscription, open Google Play Store on your Android device, tap your profile, then Payments & subscriptions.
                    </p>
                  </div>
                </div>
              </div>
              <button
                onClick={() => window.open('https://play.google.com/store/account/subscriptions', '_blank')}
                className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-[#34A853] text-white hover:opacity-90 transition-opacity text-sm font-medium"
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 0 1-.61-.92V2.734a1 1 0 0 1 .609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.198l2.807 1.626a1 1 0 0 1 0 1.73l-2.807 1.626L15.206 12l2.492-2.491zM5.864 2.658L16.8 8.99l-2.302 2.302-8.634-8.634z"/>
                </svg>
                Manage in Google Play
                <ExternalLink className="w-3.5 h-3.5" />
              </button>
            </div>
          ) : (
            <button
              onClick={onManage}
              disabled={loading}
              className="mt-4 w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border border-border hover:bg-accent transition-colors text-sm font-medium disabled:opacity-50"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <CreditCard className="w-4 h-4" />
                  Manage Subscription
                </>
              )}
            </button>
          )}
        </>
      )}
    </div>
  )
}

function PricingModal({ isOpen, onClose, onSelectPlan, loading }) {
  if (!isOpen) return null

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-50" onClick={onClose} />
      <div className="fixed inset-x-4 top-[10%] max-w-3xl mx-auto z-50 animate-scale-in">
        <div className="bg-card rounded-2xl border border-border shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="p-6 border-b border-border text-center">
            <h2 className="text-2xl font-bold">Upgrade to Premium</h2>
            <p className="text-muted-foreground mt-1">
              Unlock unlimited potential for your knowledge base
            </p>
          </div>

          {/* Plans */}
          <div className="p-6 grid sm:grid-cols-2 gap-4">
            {/* Monthly Plan */}
            <div className="p-5 rounded-xl border-2 border-border hover:border-primary/50 transition-colors">
              <div className="flex items-center gap-2 mb-3">
                <Crown className="w-5 h-5 text-primary" />
                <span className="font-semibold">Monthly</span>
              </div>
              <div className="mb-4">
                <span className="text-3xl font-bold">$8.99</span>
                <span className="text-muted-foreground">/month</span>
              </div>
              <ul className="space-y-2 mb-5">
                {PLANS.premium.features.map((feature, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm">
                    <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                    {feature}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => onSelectPlan(PLANS.premium.priceId)}
                disabled={loading}
                className="w-full py-2.5 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors font-medium disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Choose Monthly'}
              </button>
            </div>

            {/* Yearly Plan */}
            <div className="p-5 rounded-xl border-2 border-primary bg-primary/5 relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-medium">
                Best Value
              </div>
              <div className="flex items-center gap-2 mb-3">
                <Crown className="w-5 h-5 text-amber-500" />
                <span className="font-semibold">Yearly</span>
              </div>
              <div className="mb-4">
                <span className="text-3xl font-bold">$80.99</span>
                <span className="text-muted-foreground">/year</span>
                <span className="ml-2 px-2 py-0.5 rounded bg-green-500/10 text-green-600 text-xs font-medium">
                  Save $27
                </span>
              </div>
              <ul className="space-y-2 mb-5">
                <li className="flex items-center gap-2 text-sm">
                  <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                  Everything in Monthly
                </li>
                <li className="flex items-center gap-2 text-sm">
                  <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                  2 months free
                </li>
                <li className="flex items-center gap-2 text-sm">
                  <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                  Priority email support
                </li>
              </ul>
              <button
                onClick={() => onSelectPlan(PLANS.premium_yearly.priceId)}
                disabled={loading}
                className="w-full py-2.5 rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:opacity-90 transition-opacity font-medium disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Choose Yearly'}
              </button>
            </div>
          </div>

          {/* Footer */}
          <div className="p-4 border-t border-border bg-muted/30 text-center">
            <p className="text-xs text-muted-foreground">
              Secure payment powered by Stripe. Cancel anytime.
            </p>
          </div>
        </div>
      </div>
    </>
  )
}

// Get 2-letter initials from user name or email (matching Flutter pattern)
function getInitials(user) {
  if (user?.displayName) {
    const names = user.displayName.trim().split(' ')
    if (names.length >= 2) {
      return `${names[0][0]}${names[1][0]}`.toUpperCase()
    } else if (names.length === 1 && names[0].length > 0) {
      return names[0][0].toUpperCase()
    }
  }
  if (user?.email) {
    return user.email[0].toUpperCase()
  }
  return 'U'
}

export default function SettingsPage() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [darkMode, setDarkMode] = useState(
    document.documentElement.classList.contains('dark')
  )
  const [avatarError, setAvatarError] = useState(false)

  // Subscription state
  const [subscription, setSubscription] = useState(null)
  const [usage, setUsage] = useState(null)
  const [subscriptionLoading, setSubscriptionLoading] = useState(true)
  const [actionLoading, setActionLoading] = useState(false)
  const [showPricingModal, setShowPricingModal] = useState(false)
  const [subscriptionMessage, setSubscriptionMessage] = useState(null)

  // Load subscription status on mount
  useEffect(() => {
    loadSubscriptionData()

    // Check for subscription callback params
    const subscriptionStatus = searchParams.get('subscription')
    if (subscriptionStatus === 'success') {
      setSubscriptionMessage({
        type: 'success',
        text: 'Welcome to Premium! Your subscription is now active.',
      })
      // Reload subscription data
      loadSubscriptionData()
    } else if (subscriptionStatus === 'cancelled') {
      setSubscriptionMessage({
        type: 'info',
        text: 'Subscription checkout was cancelled.',
      })
    }
  }, [])

  const loadSubscriptionData = async () => {
    setSubscriptionLoading(true)
    try {
      const response = await getCurrentUser()

      // Debug: log the full response to understand structure
      console.log('Full API response:', response)

      // Try different possible response structures
      const userData = response.user || response.data?.user || response

      // Debug: log the user data to see available fields
      console.log('User data from API:', userData)

      // Get payment provider directly from API
      // Values: 'appstore' | 'stripe' | 'google_play' | null
      const paymentProviderRaw = userData?.payment_provider || null
      const tier = userData?.tier || ''

      console.log('Tier:', tier, '| Payment provider:', paymentProviderRaw)

      // Determine if user is on a premium plan
      // The tier could be "free", "premium", or a product ID like "app.sigmanote.premium.monthly"
      const isPremiumTier = tier && tier !== 'free' && (
        tier.includes('premium') ||
        tier.includes('pro') ||
        tier.startsWith('price_') ||
        tier.startsWith('app.') ||
        tier.startsWith('com.') ||
        paymentProviderRaw !== null // If there's a payment provider, it's a paid subscription
      )

      // If payment provider is null but user has premium, default to App Store
      const paymentProvider = isPremiumTier && !paymentProviderRaw ? 'appstore' : paymentProviderRaw

      // Extract a friendly subscription name from the tier
      let subscriptionName = 'Premium'
      if (tier.includes('monthly')) {
        subscriptionName = 'Premium Monthly'
      } else if (tier.includes('yearly') || tier.includes('annual')) {
        subscriptionName = 'Premium Yearly'
      }

      // Set subscription info from user data
      setSubscription({
        plan: isPremiumTier ? 'premium' : 'free',
        subscriptionName: subscriptionName,
        expiresAt: userData?.license_expires_at || userData?.licenseExpiresAt,
        paymentProvider: paymentProvider,
      })

      // Set usage stats from user data
      setUsage({
        contentCount: userData?.content_count || 0,
        contentCap: userData?.content_cap || 50,
        chatCount: userData?.chat_count || 0,
        chatCap: userData?.chat_cap || 10,
        storageCount: userData?.space_count || 0,
        storageCap: userData?.space_cap || 100,
      })
    } catch (err) {
      console.error('Failed to load user data:', err)
      // Fallback to free plan on error
      setSubscription({ plan: 'free', paymentProvider: null })
      setUsage({
        contentCount: 0,
        contentCap: 50,
        chatCount: 0,
        chatCap: 10,
        storageCount: 0,
        storageCap: 100,
      })
    } finally {
      setSubscriptionLoading(false)
    }
  }

  const handleUpgrade = () => {
    setShowPricingModal(true)
  }

  const handleSelectPlan = async (priceId) => {
    setActionLoading(true)
    try {
      const result = await createCheckoutSession(priceId)
      if (result.url) {
        window.location.href = result.url
      } else {
        throw new Error('No checkout URL returned')
      }
    } catch (err) {
      console.error('Failed to create checkout session:', err)
      setShowPricingModal(false)

      // Provide more specific error message based on error type
      let errorMessage = 'Unable to start checkout. Please try again later.'
      if (err.message?.includes('Session expired')) {
        errorMessage = 'Your session has expired. Please sign in again.'
      } else if (err.message?.includes('network') || err.message?.includes('fetch')) {
        errorMessage = 'Network error. Please check your connection and try again.'
      } else if (err.message?.includes('500') || err.message?.includes('server')) {
        errorMessage = 'Our payment service is temporarily unavailable. Please try again later or contact support.'
      }

      setSubscriptionMessage({
        type: 'error',
        text: errorMessage,
      })
    } finally {
      setActionLoading(false)
    }
  }

  const handleManageSubscription = async () => {
    setActionLoading(true)
    try {
      const result = await createPortalSession()
      if (result.url) {
        window.location.href = result.url
      }
    } catch (err) {
      console.error('Failed to create portal session:', err)
      setSubscriptionMessage({
        type: 'error',
        text: 'Failed to open subscription portal. Please try again.',
      })
    } finally {
      setActionLoading(false)
    }
  }

  const toggleDarkMode = () => {
    const newMode = !darkMode
    setDarkMode(newMode)
    if (newMode) {
      document.documentElement.classList.add('dark')
      localStorage.setItem('theme', 'dark')
    } else {
      document.documentElement.classList.remove('dark')
      localStorage.setItem('theme', 'light')
    }
  }

  const handleLogout = async () => {
    await logOut()
    navigate('/login')
  }

  return (
    <div className="p-6 max-w-2xl mx-auto animate-fade-in">
      <h1 className="text-2xl font-bold mb-6">Settings</h1>

      {/* Subscription Message */}
      {subscriptionMessage && (
        <div
          className={`mb-6 p-4 rounded-xl flex items-center gap-3 border ${
            subscriptionMessage.type === 'success'
              ? 'bg-green-500/10 border-green-500/30 text-green-600 dark:text-green-400'
              : subscriptionMessage.type === 'error'
              ? 'bg-red-500/10 border-red-500/30 text-red-600 dark:text-red-400'
              : 'bg-primary/10 border-primary/30 text-primary'
          }`}
        >
          {subscriptionMessage.type === 'success' ? (
            <Check className="w-5 h-5 flex-shrink-0" />
          ) : subscriptionMessage.type === 'error' ? (
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
          ) : (
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
          )}
          <p className="flex-1 font-medium">{subscriptionMessage.text}</p>
          <button
            onClick={() => setSubscriptionMessage(null)}
            className="p-1.5 hover:bg-black/10 dark:hover:bg-white/10 rounded-lg transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Profile section */}
      <SettingSection title="Account">
        <div className="p-4 flex items-center gap-4 border-b border-border">
          {user?.photoURL && user.photoURL.trim() !== '' && !avatarError ? (
            <img
              src={user.photoURL}
              alt={user.displayName || 'User avatar'}
              className="w-16 h-16 rounded-2xl object-cover bg-muted"
              onError={() => setAvatarError(true)}
              referrerPolicy="no-referrer"
            />
          ) : (
            <div className="w-16 h-16 rounded-2xl bg-primary/20 flex items-center justify-center">
              <span className="text-2xl font-bold text-primary">
                {getInitials(user)}
              </span>
            </div>
          )}
          <div className="flex-1">
            <p className="font-semibold text-lg">{user?.displayName || 'User'}</p>
            <p className="text-muted-foreground">{user?.email}</p>
          </div>
        </div>
        <SettingItem
          icon={User}
          label="Edit Profile"
          description="Update your name and photo"
        />
      </SettingSection>

      {/* Subscription */}
      <SettingSection title="Subscription">
        {subscriptionLoading ? (
          <div className="p-8 flex items-center justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        ) : (
          <>
            <SubscriptionCard
              subscription={subscription}
              usage={usage}
              onUpgrade={handleUpgrade}
              onManage={handleManageSubscription}
              loading={actionLoading}
            />
            <div className="border-t border-border">
              <SettingItem
                icon={RefreshCw}
                label="Refresh Subscription"
                description="Sync your subscription status"
                onClick={loadSubscriptionData}
              />
            </div>
          </>
        )}
      </SettingSection>

      {/* Preferences */}
      <SettingSection title="Preferences">
        <SettingItem
          icon={darkMode ? Moon : Sun}
          label="Dark Mode"
          description={darkMode ? 'Currently using dark theme' : 'Currently using light theme'}
          onClick={toggleDarkMode}
          action={
            <div
              className={`w-12 h-7 rounded-full p-1 transition-colors ${
                darkMode ? 'bg-primary' : 'bg-muted'
              }`}
            >
              <div
                className={`w-5 h-5 rounded-full bg-white shadow-sm transition-transform ${
                  darkMode ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </div>
          }
        />
        <SettingItem
          icon={Bell}
          label="Notifications"
          description="Manage notification preferences"
        />
      </SettingSection>

      {/* Support */}
      <SettingSection title="Support">
        <SettingItem
          icon={HelpCircle}
          label="Help Center"
          description="FAQs and documentation"
          onClick={() => window.open('https://sigmanote.app/help', '_blank')}
          action={<ExternalLink className="w-4 h-4 text-muted-foreground" />}
        />
        <SettingItem
          icon={Shield}
          label="Privacy Policy"
          onClick={() => window.open('https://signifyid.com/privacy', '_blank')}
          action={<ExternalLink className="w-4 h-4 text-muted-foreground" />}
        />
      </SettingSection>

      {/* Danger zone */}
      <SettingSection title="Account Actions">
        <SettingItem
          icon={LogOut}
          label="Sign Out"
          description="Sign out of your account"
          onClick={handleLogout}
          danger
        />
      </SettingSection>

      {/* Version */}
      <p className="text-center text-sm text-muted-foreground mt-8">
        SigmaNote Web v0.1.0
      </p>

      {/* Pricing Modal */}
      <PricingModal
        isOpen={showPricingModal}
        onClose={() => setShowPricingModal(false)}
        onSelectPlan={handleSelectPlan}
        loading={actionLoading}
      />
    </div>
  )
}
